import React, { useState } from 'react';
import usePersistentState from '../hooks/usePersistentState';
import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import dataService from '../services/dataService';
import OrderDetailsModal from './OrderDetailsModal';

const DataTable = ({ data, sortField, sortDirection, onSortChange, currentPage, onPageChange, onExportPDF, onExportCSV }) => {
  const [itemsPerPage, setItemsPerPage] = usePersistentState('ordersList.itemsPerPage', 25);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Paginação
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const handleSort = (field) => {
    onSortChange(field);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4" /> : 
      <ChevronDownIcon className="w-4 h-4" />;
  };

  const getStatusBadge = (status) => {
    const normalize = (s) => {
      if (s === null || s === undefined) return 'Não informado';
      const str = typeof s === 'string' ? s : String(s);
      const map = {
        '0': 'Aguardando diagnóstico',
        '1': 'Orçada/aguardando aprovação',
        '2': 'Aguardando início da execução',
        '3': 'Em execução',
        '4': 'Parada/aguardando material',
        '5': 'Concluída',
        '6': 'Entregue',
        '7': 'Orçamento não aprovado',
        '9': 'Cancelada'
      };
      return map[str] || str;
    };
    const value = normalize(status);
    if (!value) return <span className="badge-warning">Não informado</span>;
    if (value.includes('Concluída')) return <span className="badge-success">{value}</span>;
    if (value.includes('Aguardando')) return <span className="badge-warning">{value}</span>;
    if (value.includes('Parada/aguardando material')) return <span className="badge-warning">{value}</span>;
    if (value.includes('Entregue')) return <span className="badge-success">{value}</span>;
    if (value.includes('Orçamento não aprovado')) return <span className="badge-danger">{value}</span>;
    if (value.includes('Cancelada')) return <span className="badge-danger">{value}</span>;
    if (value.includes('Pendente')) return <span className="badge-danger">{value}</span>;
    return <span className="badge-info">{value}</span>;
  };

  

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (newPage) => {
    onPageChange(Math.max(1, Math.min(newPage, totalPages)));
  };

  return (
    <>
    <div className="card-dark">
      {/* Header com informações e exportação */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <div className="text-dark-text-secondary">
          Mostrando {startIndex + 1}-{Math.min(endIndex, data.length)} de {data.length} ordens
        </div>
        <div className="flex items-center gap-4">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              onPageChange(1);
            }}
            className="input-dark text-sm"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
          {onExportPDF && (
            <button
              onClick={onExportPDF}
              className="btn-secondary p-2 hover:bg-primary hover:text-white transition-colors"
              title="Exportar para PDF"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
            </button>
          )}
          {onExportCSV && (
            <button
              onClick={onExportCSV}
              className="btn-secondary p-2 hover:bg-primary hover:text-white transition-colors"
              title="Exportar para CSV"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto custom-scrollbar">
        <table className="table-dark">
          <thead>
            <tr>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Ord.Ser' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Ord.Ser')}
              >
                <div className="flex items-center gap-2">
                  Ordem
                  {getSortIcon('Ord.Ser')}
                </div>
              </th>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Data Inclusao' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Data Inclusao')}
              >
                <div className="flex items-center gap-2">
                  Data
                  {getSortIcon('Data Inclusao')}
                </div>
              </th>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Descrição' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Descrição')}
              >
                <div className="flex items-center gap-2">
                  Descrição
                  {getSortIcon('Descrição')}
                </div>
              </th>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Problema' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Problema')}
              >
                <div className="flex items-center gap-2">
                  Problema
                  {getSortIcon('Problema')}
                </div>
              </th>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Técnico' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Técnico')}
              >
                <div className="flex items-center gap-2">
                  Técnico
                  {getSortIcon('Técnico')}
                </div>
              </th>
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Posição' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Posição')}
              >
                <div className="flex items-center gap-2">
                  Status
                  {getSortIcon('Posição')}
                </div>
              </th>
              
              <th 
                className={`cursor-pointer hover:bg-gray-600 select-none ${sortField === 'Vlr Total orçado' ? 'bg-gray-600' : ''}`}
                onClick={() => handleSort('Vlr Total orçado')}
              >
                <div className="flex items-center gap-2">
                  Vlr Total orçado
                  {getSortIcon('Vlr Total orçado')}
                </div>
              </th>
              <th className="text-center">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((order, index) => (
              <tr key={`${order['Ord.Ser']}-${index}`}>
                <td className="font-medium text-primary">
                  {dataService.safeValue(order['Ord.Ser'])}
                </td>
                <td>
                  {dataService.formatDate(order['Data Inclusao'])}
                </td>
                <td className="max-w-xs truncate" title={order['Descrição']}>
                  {dataService.safeValue(order['Descrição'])}
                </td>
                <td className="max-w-xs truncate" title={order['Problema']}>
                  {dataService.safeValue(order['Problema'])}
                </td>
                <td>
                  {dataService.safeValue(order['Técnico'])}
                </td>
                <td>
                  {getStatusBadge(order['Posição'])}
                </td>
                
                <td className="text-right font-mono">
                  {dataService.formatCurrency(
                    (parseFloat(order['Vlr.cobrado ser']) || 0) +
                    (parseFloat(order['Vlr.cobrado mat']) || 0) +
                    (parseFloat(order['Vlr.cobrado itens']) || 0)
                  )}
                </td>
                <td className="text-center">
                  <button
                    onClick={() => handleViewDetails(order)}
                    className="btn-secondary p-2 hover:bg-primary hover:text-white transition-colors"
                    title="Ver detalhes da ordem"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-gray-700 flex justify-between items-center">
          <div className="text-dark-text-secondary text-sm">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    pageNum === currentPage
                      ? 'bg-primary text-white'
                      : 'bg-gray-700 text-dark-text-secondary hover:bg-gray-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
    
    <OrderDetailsModal 
      order={selectedOrder}
      isOpen={isModalOpen}
      onClose={handleCloseModal}
    />
    </>
  );
};

export default DataTable;
